import { useEffect, useRef, useState } from 'react';
import { useRealtimeManager } from './useRealtimeManager';

interface SelectiveSubscriptionOptions {
  enabled?: boolean;
  debounceMs?: number;
  priority?: 'high' | 'medium' | 'low';
  onlyWhenVisible?: boolean;
  onError?: (error: any) => void;
}

interface SubscriptionConfig {
  table: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  schema?: string;
}

export const useSelectiveSubscription = (
  config: SubscriptionConfig,
  callback: (payload: any) => void,
  options: SelectiveSubscriptionOptions = {}
) => {
  const { subscribe } = useRealtimeManager();
  const [isVisible, setIsVisible] = useState(true);
  const cleanupRef = useRef<(() => void) | null>(null);
  const callbackRef = useRef(callback);
  
  const {
    enabled = true,
    debounceMs = 500,
    priority = 'medium',
    onlyWhenVisible = true,
    onError
  } = options;

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Track page visibility
  useEffect(() => {
    if (!onlyWhenVisible) return;

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    const handleFocus = () => setIsVisible(true);
    const handleBlur = () => setIsVisible(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Set initial state
    setIsVisible(!document.hidden);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [onlyWhenVisible]);

  // Manage subscription based on conditions
  useEffect(() => {
    // Cleanup previous subscription
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    // Only subscribe if enabled and (visible or visibility doesn't matter)
    const shouldSubscribe = enabled && (isVisible || !onlyWhenVisible);
    
    if (shouldSubscribe) {
      cleanupRef.current = subscribe(
        config,
        (payload) => {
          if (callbackRef.current) {
            callbackRef.current(payload);
          }
        },
        {
          debounceMs,
          priority,
          onError
        }
      );
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [enabled, isVisible, onlyWhenVisible, config.table, config.event, config.filter, debounceMs, priority]);

  return {
    isVisible,
    isSubscribed: !!(cleanupRef.current && enabled && (isVisible || !onlyWhenVisible))
  };
};